import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CardBrand } from 'shared/types/graphql';
import { StateSliceName } from 'state/StateSliceName';

export enum PaymentGrouping {
  SingleType = 'singleType',
  CardType = 'cardType',
}

export enum WritebackConfigurationStatus {
  Incomplete = 'Incomplete',
  Complete = 'Complete',
  None = 'None',
}

interface PaymentSettingsState {
  paymentGrouping: PaymentGrouping;
  employeeId: string | null;
  selectedCardBrands: CardBrand[];
  amexPimsPaymentTypeId: string | null;
  discoverPimsPaymentTypeId: string | null;
  mastercardPimsPaymentTypeId: string | null;
  visaPimsPaymentTypeId: string | null;
  defaultPimsPaymentTypeId: string | null;
  paymentTypesInitialized: boolean;
  configurationStatus: WritebackConfigurationStatus;
  incompleteFields: string[];
}

const initialState: PaymentSettingsState = {
  paymentGrouping: PaymentGrouping.SingleType,
  employeeId: null,
  selectedCardBrands: [CardBrand.Unknown],
  amexPimsPaymentTypeId: null,
  discoverPimsPaymentTypeId: null,
  mastercardPimsPaymentTypeId: null,
  visaPimsPaymentTypeId: null,
  defaultPimsPaymentTypeId: null,
  paymentTypesInitialized: false,
  configurationStatus: WritebackConfigurationStatus.None,
  incompleteFields: [],
};

const paymentSettingsSlice = createSlice({
  name: StateSliceName.PaymentSettings,
  initialState,
  reducers: {
    updatePaymentGrouping: (state, action: PayloadAction<PaymentGrouping>) => {
      state.paymentGrouping = action.payload;
    },
    updateSelectedEmployee: (state, action: PayloadAction<string | null>) => {
      state.employeeId = action.payload;
    },
    addSelectedCardBrand: (state, action: PayloadAction<CardBrand>) => {
      if (!state.selectedCardBrands.includes(action.payload)) {
        state.selectedCardBrands.push(action.payload);
      }
    },
    removeSelectedCardBrand: (state, action: PayloadAction<CardBrand>) => {
      state.selectedCardBrands = state.selectedCardBrands.filter((brand) => brand !== action.payload);
      switch (action.payload) {
        case CardBrand.Amex:
          state.amexPimsPaymentTypeId = null;
          break;
        case CardBrand.Discover:
          state.discoverPimsPaymentTypeId = null;
          break;
        case CardBrand.Mastercard:
          state.mastercardPimsPaymentTypeId = null;
          break;
        case CardBrand.Visa:
          state.visaPimsPaymentTypeId = null;
          break;
        default:
          break;
      }
    },
    clearSelectedCardBrands: (state) => {
      state.selectedCardBrands = [CardBrand.Unknown];
    },
    setPimsPaymentType: (state, action: PayloadAction<{ cardBrand: CardBrand; paymentTypeId: string | null }>) => {
      const { cardBrand, paymentTypeId } = action.payload;
      switch (cardBrand) {
        case CardBrand.Amex:
          state.amexPimsPaymentTypeId = paymentTypeId;
          break;
        case CardBrand.Discover:
          state.discoverPimsPaymentTypeId = paymentTypeId;
          break;
        case CardBrand.Mastercard:
          state.mastercardPimsPaymentTypeId = paymentTypeId;
          break;
        case CardBrand.Visa:
          state.visaPimsPaymentTypeId = paymentTypeId;
          break;
        case CardBrand.Unknown:
          state.defaultPimsPaymentTypeId = paymentTypeId;
          break;
        default:
          break;
      }
    },
    setPaymentTypesInitialized: (state, action: PayloadAction<boolean>) => {
      state.paymentTypesInitialized = action.payload;
    },
    setConfigurationStatus: (state, action: PayloadAction<WritebackConfigurationStatus>) => {
      state.configurationStatus = action.payload;
    },
    setIncompleteFields: (state, action: PayloadAction<string[]>) => {
      state.incompleteFields = action.payload;
    },
    cancelChanges: () => initialState,
  },
});

export const {
  updatePaymentGrouping,
  updateSelectedEmployee,
  addSelectedCardBrand,
  removeSelectedCardBrand,
  clearSelectedCardBrands,
  setPimsPaymentType,
  setPaymentTypesInitialized,
  setConfigurationStatus,
  setIncompleteFields,
  cancelChanges,
} = paymentSettingsSlice.actions;

export default paymentSettingsSlice.reducer;
